import React from "react";
import PropTypes from "prop-types";
import { NavigatorAction, BookUtils, Dispatcher } from "../../../../flux";

export default class PageRange extends React.Component {
  constructor(props) {
    super(props);
    const reading = this.props.reading;
    const state = {
      start: reading.start,
      end: reading.end,
      summary: reading.summary,
      description: reading.description,
      order: reading.order || 1,
      type: reading.type || this.props.book.format,
      isCustomDesc: !!reading.description,
      pageBreaks: this.props.book.getPageBreaks() || [],
      exclusive:
        typeof reading.exclusive === "boolean" ? reading.exclusive : true,
      rangeType:
        reading.rangeType || this.props.book.format === "pbk"
          ? "page_index"
          : "cfi",
      saving: false,
      navigating: false
    };
    this.state = Object.assign(state, this.props.savedState);
  }

  componentWillUnmount() {
    this.props.book.setAutofocus(true);
  }

  componentDidMount() {
    this.focusOnMount.focus();
    Dispatcher.bindOnce(Dispatcher.BOOK_PAGE_READY, () => {
      this.setState({ pageBreaks: this.props.book.getPageBreaks() });
    });
    this.props.book.setAutofocus(false);
    if (this.state.start) {
      if (this.state.rangeType === "cfi") {
        this.props.book.goTo({ cfi: this.state.start });
      } else if (this.state.rangeType === "page_index") {
        this.props.book.goTo({
          page: BookUtils.getLabelFromIndex(this.props.isbn, this.state.start)
        });
      }
    }
    this.loadStartEnd();
  }

  loadStartEnd() {
    const reading = this.props.reading;
    let start = "";
    let end = "";
    if (reading.range_type === "cfi") {
      if (reading.start && reading.start.indexOf("!") === -1) {
        start = BookUtils.getPageForCFI(this.props.isbn, `${reading.start}!/4`);
      } else {
        start = BookUtils.getPageForCFI(this.props.isbn, reading.start);
      }
      if (reading.exclusive) {
        const endPage = BookUtils.getPreviousPageFromCFI(
          this.props.isbn,
          reading.end
        );
        end = (endPage && endPage.label) || "";
      } else {
        end = BookUtils.getPageForCFI(this.props.isbn, reading.end);
      }
    } else if (reading.range_type === "page_index") {
      start = BookUtils.getLabelFromIndex(this.props.isbn, reading.start);
      end = BookUtils.getLabelFromIndex(this.props.isbn, reading.end);
    }
    this.setState({ start, end }, () => this.validate());
  }

  back() {
    NavigatorAction.pop();
    const newState = {};
    newState[this.props.route] = null;
    this.props.saveState(newState);
  }

  _createCfiReading() {
    const meta = [{ tooling: "page-range" }];
    if (this.props.assignment.uuid) {
      const startCfi = BookUtils.getCFIForPageLabel(
        this.props.isbn,
        this.state.start
      );
      const saveReading = this._createOrUpdateReading.bind(this, meta);
      this.setState({ saving: true });
      this.props.book.goTo({
        page: this.state.start,
        callback: () => {
          if (startCfi.indexOf("!") === -1) {
            this._savePreview("/4", saveReading);
          } else {
            this._savePreview(startCfi.split("!").pop(), saveReading);
          }
        }
      });
    } else {
      this.props
        .createOrUpdateAssignment()
        .then(() => this._createCfiReading(meta));
    }
  }

  _createPageIndexReding() {
    const meta = [{ tooling: "page-range" }];
    const saveReading = this._createOrUpdateReading.bind(this, meta);
    if (this.props.assignment.uuid) {
      const startCfi = BookUtils.getCFIForPageLabel(
        this.props.isbn,
        this.state.start
      );
      this.setState({ saving: true });
      this.props.book.goTo({
        page: this.state.start,
        callback: () => {
          if (startCfi) {
            if (startCfi.indexOf("!") === -1) {
              return this._savePreview("/4", saveReading);
            } else {
              return this._savePreview(startCfi.split("!").pop(), saveReading);
            }
          }
          saveReading();
          this.setState({ saving: false });
        }
      });
    } else {
      this.props
        .createOrUpdateAssignment()
        .then(() => this._createPageIndexReding(meta));
    }
  }

  _createReading() {
    if (this.state.ready) {
      if (this.state.rangeType === "cfi") {
        this._createCfiReading();
      } else if (this.state.rangeType === "page_index") {
        this._createPageIndexReding();
      }
    }
  }

  _buildReading() {
    if (this.state.rangeType === "cfi") {
      const startCfi = BookUtils.getCFIForPageLabel(
        this.props.isbn,
        this.state.start
      );
      const endCfi = BookUtils.getCFIForPageLabel(
        this.props.isbn,
        this.state.end
      );
      const nextCfi = BookUtils.getNextPageFromCFI(this.props.isbn, endCfi);
      const exclusive = !BookUtils.isLastPage(this.props.isbn, endCfi);

      return {
        start: startCfi,
        end: nextCfi && nextCfi.cfi,
        summary: this.state.summary,
        description:
          this.state.description.trim().substring(0, 255) || undefined,
        order: this.state.order,
        type: this.state.type,
        exclusive: exclusive,
        range_type: this.state.rangeType
      };
    } else if (this.state.rangeType === "page_index") {
      const startIndex = BookUtils.getIndexFromLabel(
        this.props.isbn,
        this.state.start
      );
      const endIndex = BookUtils.getIndexFromLabel(
        this.props.isbn,
        this.state.end
      );
      return {
        start: `${startIndex}`,
        end: `${endIndex}`,
        summary: this.state.summary,
        description:
          this.state.description.trim().substring(0, 255) || undefined,
        order: this.state.order,
        type: this.state.type,
        exclusive: false,
        range_type: this.state.rangeType
      };
    }
  }

  _createOrUpdateReading(meta) {
    if (!this.props.saving && this.props.feenyReady) {
      this.setState({ saving: false });
      const reading = this._buildReading();
      if (this.props.reading && this.props.reading.uuid) {
        this.props.updateReading(this.props.reading.uuid, reading);
      } else {
        this.props.createReading(reading, meta);
      }
      this.back();
    }
  }

  _savePreview(cfi, callback) {
    this.props.book.viewer.Book.getPreview(cfi, response => {
      if (response.data) {
        this.setState(
          {
            summary: response.data.preview
          },
          () => {
            const newState = {};
            newState[this.props.route] = this.state;
            this.props.saveState(newState);
            if (typeof callback === "function") {
              callback();
            }
          }
        );
      }
    });
  }

  setDescription() {
    const { start, end, isCustomDesc, ready } = this.state;
    if (ready && !isCustomDesc) {
      let description = "Read pages " + start + " to " + end;
      if (start === end) {
        description = "Read page " + start;
      }
      this.setState({ description }, () => {
        const newState = {};
        newState[this.props.route] = this.state;
        this.props.saveState(newState);
      });
    }
    if (!ready && !isCustomDesc) {
      this.setState({ description: "" }, () => {
        const newState = {};
        newState[this.props.route] = this.state;
        this.props.saveState(newState);
      });
    }
  }

  validate() {
    this.setState(
      {
        ready: this._validPageRange()
      },
      () => {
        this.setDescription();
      }
    );
  }

  _validPageRange() {
    const { start, end } = this.state;
    const isValid = this.checkPage(start) && this.checkPage(end);
    if (isValid) {
      const startPage = this.state.pageBreaks.find(p => p.label === start);
      const endPage = this.state.pageBreaks.find(p => p.label === end);
      if (this.state.rangeType === "cfi") {
        return (
          startPage &&
          endPage &&
          Jigsaw.CFIUtils.compareCFIs(startPage.cfi, endPage.cfi) <= 0
        );
      } else if (this.state.rangeType === "page_index") {
        const isbn = this.props.isbn;
        const startIndex = BookUtils.getIndexFromLabel(isbn, startPage.label);
        const endIndex = BookUtils.getIndexFromLabel(isbn, endPage.label);
        return startIndex <= endIndex;
      }
    }
  }

  checkPage(page) {
    return (
      page &&
      this.state.pageBreaks &&
      this.state.pageBreaks.some(pageBreak => pageBreak.label === page)
    );
  }

  navigateToPage(page) {
    if (!this.state.saving) {
      if (this.state.pageBreaks && this.state.pageBreaks.length === 0) {
        this.setState({ pageBreaks: this.props.book.getPageBreaks() }, () => {
          this.navigateToPage(page);
        });
      } else {
        this.validate();
        if (this.checkPage(page)) {
          this.setState({ navigating: true });
          this.props.book.goTo({
            page,
            callback: () => {
              this.setState({ navigating: false });
            }
          });
        }
        if (this.state.start && this.state.end && !this._validPageRange()) {
          Fraction.Helpers.toast(i18n.t("print.invalid-range"), {
            type: "error"
          });
        }
      }
    }
  }

  _goToPageKeyUp(event, savePreview) {
    event.preventDefault();
    event.stopPropagation();
    if (event.keyCode === 13) {
      const page = $(event.currentTarget).val();
      this.navigateToPage(page, savePreview);
    }
  }

  _onChangeDescription(value) {
    this.setState({
      description: value.substring(0, 255),
      isCustomDesc: value.length > 0
    });
  }

  _onBlur(currentValue) {
    this.navigateToPage(this.state.start);
  }

  _onPageChange(field, value) {
    const fieldState = {};
    fieldState[field] = value;
    this.setState(fieldState, () => {
      const newState = {};
      newState[this.props.route] = this.state;
      this.props.saveState(newState);
      this.validate();
    });
  }

  render() {
    const placeholderExample = i18n.t("assignments.start-end-ie");

    return (
      <div id="reading-new">
        <div className="instructions">
          <h3
            className="title"
            ref={input => {
              this.focusOnMount = input;
            }}
            tabIndex={-1}
          >
            {i18n.t("assignments.page-range")}
          </h3>
          <p>{i18n.t("assignments.page-range-desc")}</p>
        </div>

        <div className="reading-form">
          <div className="input-group input-inline">
            <div className="input-container">
              <label className="label">
                {i18n.t("assignments.page-start")}
              </label>
              <input
                className="input"
                type="text"
                name="page-start"
                id="page-start"
                value={this.state.start}
                onFocus={event => this.navigateToPage(this.state.start)}
                onKeyUp={event => this._goToPageKeyUp(event)}
                onBlur={event => this.navigateToPage(this.state.start)}
                onChange={event =>
                  this._onPageChange("start", event.target.value)
                }
              />
            </div>
            <div className="text-middle">{i18n.t("base.to")}</div>
            <div className="input-container">
              <label className="label">{i18n.t("assignments.page-end")}</label>
              <input
                className="input"
                type="text"
                name="page-end"
                id="page-end"
                value={this.state.end}
                onFocus={event => this.navigateToPage(this.state.end)}
                onKeyUp={event => this._goToPageKeyUp(event)}
                onBlur={event => this.navigateToPage(this.state.end)}
                onChange={event =>
                  this._onPageChange("end", event.target.value)
                }
              />
            </div>
          </div>
          <div className="input-container">
            <label className="label">
              {i18n.t("assignments.reading-name")} (
              {i18n.t("base.optional").toLowerCase()})
            </label>
            <input
              className="input"
              type="text"
              name="reading-name"
              id="reading-name"
              placeholder={placeholderExample}
              maxLength={255}
              value={this.state.description}
              onChange={event => this._onChangeDescription(event.target.value)}
            />
          </div>
          <div className="form-actions">
            <button
              className={`${
                this.state.ready && !this.state.navigating
                  ? "submit"
                  : "transparentButton"
              }`}
              disabled={!this.state.ready && !this.state.navigating}
              onClick={this._createReading.bind(this)}
            >
              {this.state.saving
                ? `${i18n.t("base.saving")}...`
                : i18n.t("base.save")}
            </button>
            <button className="cancel noButton" onClick={() => this.back()}>
              {i18n.t("base.cancel")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

PageRange.propTypes = {
  isbn: PropTypes.string.isRequired,
  book: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  createReading: PropTypes.func.isRequired,
  createOrUpdateAssignment: PropTypes.func.isRequired,
  assignment: PropTypes.object,
  reading: PropTypes.object
};

PageRange.defaultProps = {
  reading: {}
};
